import HeadlineComponent, { Props as HeadlineProps } from '../headline/headline.component';
import { ReactElement, memo, useState } from 'react';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import styles from './paragraph.component.module.scss';
import useIsCSR from '@spa/lib/react/hooks/useIsCSR';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';

export interface Props {
  readonly contentHtml: string;
  readonly collapsable: boolean;
  readonly optionalHeadline: HeadlineProps | null;
  readonly dateFrom?: number | null;
  readonly dateTo?: number | null;
  readonly editorMode?: boolean;
}

function Paragraph({
  contentHtml,
  collapsable,
  optionalHeadline,
  dateTo = null,
  dateFrom = null,
  editorMode = false
}: Props): ReactElement {
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('paragraph');
  const [isExpanded, setIsExpanded] = useState(false);
  const searchParams = useRuntimeSearchParams();
  const isCsr: boolean = useIsCSR();
  const isRedirected: boolean = searchParams?.get('redirected') === 'true';

  let contentHtmlWithTracking: string = contentHtml;
  if (isCsr && isRedirected) {
    const parser = new DOMParser();
    const doc: Document = parser.parseFromString(contentHtml, 'text/html');
    doc.querySelectorAll('a').forEach((anchor: HTMLAnchorElement) => {
      anchor.setAttribute('data-track-id', 'redirectSubSelector');
    });
    contentHtmlWithTracking = doc.body.innerHTML;
  }

  const paragraphClassNames = classNames(styles.paragraph, {
    [styles['paragraph--collapsable']]: collapsable,
    [styles['paragraph--expanded']]: isExpanded
  });

  return (
    <article className={paragraphClassNames}>
      {editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />}
      {optionalHeadline?.headline && (
        <HeadlineComponent
          id={optionalHeadline.id}
          usedInFullWidthComponent={true}
          headline={optionalHeadline.headline}
          subHeadline={optionalHeadline.subHeadline}
          link={optionalHeadline.link}
        />
      )}
      <div className={styles.paragraph__container}>
        <div
          className={styles.paragraph__content}
          dangerouslySetInnerHTML={{ __html: contentHtmlWithTracking }}
        />
        {collapsable && <div className={styles.paragraph__gradient} />}
      </div>
      {collapsable && (
        <div className={styles['paragraph__read-more']}>
          <button
            onClick={(): void => setIsExpanded((prev) => !prev)}
            className={styles['paragraph__read-more-button']}
            aria-expanded={isExpanded}
            aria-controls="collapsable-content"
          >
            {formatMessage('readMore')}
          </button>
        </div>
      )}
    </article>
  );
}

export default memo(Paragraph);
